import React, { useMemo, useState } from 'react';
import { Check, Copy, FlaskConical, Globe, Shield } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { gatewayPathForTab, MCP_PUBLIC_URL, SETUP_STEPS, SINGLETON_ENDPOINT_ID } from '../governedMcpConstants';
import { logGovernedMcp } from '../governedMcpLogger';
import { useGovernedMcp } from '../governedMcpStore';
import type { McpEndpoint, SecuritySwitches } from '../governedMcpTypes';
import { ghostBtn, primaryBtn, ToggleRow } from '../components/GatewayUi';
import EndpointFormDrawer from '../drawers/EndpointFormDrawer';
import TestConnectionModal from '../drawers/TestConnectionModal';

export default function SetupTab({
  isLight,
  onToast,
}: {
  isLight: boolean;
  onToast: (type: 'success' | 'error' | 'info', message: string) => void;
}) {
  const { state, openConfiguration, saveEndpoint, recordTestConnection } = useGovernedMcp();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const host = state.endpoints[0];

  const progress = useMemo(() => {
    return [
      Boolean(host),
      state.clients.length > 0,
      state.policies.length > 0,
      state.published.length > 0,
      state.activity.some((item) => item.operation === 'test_connection'),
    ];
  }, [host, state]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(MCP_PUBLIC_URL);
      onToast('success', 'MCP URL copied');
      logGovernedMcp('app', 'setup_mcp_url_copied', { url: MCP_PUBLIC_URL });
    } catch {
      onToast('error', 'Could not copy to clipboard');
      logGovernedMcp('warning', 'setup_mcp_url_copy_failed', {});
    }
  };

  const patchSecurity = (patch: Partial<SecuritySwitches>) => {
    if (!host) return;
    saveEndpoint({ ...host, security: { ...host.security, ...patch } });
  };

  return (
    <div className="space-y-5">
      <div className={`rounded-xl border p-4 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}>
        <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
          Setup sequence
        </p>
        <ol className="grid grid-cols-1 md:grid-cols-5 gap-2">
          {SETUP_STEPS.map((step, index) => {
            const done = progress[index];
            return (
              <li key={step.id}>
                <button
                  type="button"
                  onClick={() => {
                    if (step.tab === 'tools') navigate(gatewayPathForTab('tools'));
                    else if (step.section) openConfiguration(step.section);
                    else openConfiguration('setup');
                  }}
                  className={`w-full text-left rounded-lg border px-3 py-2.5 ${
                    done
                      ? isLight
                        ? 'border-emerald-200 bg-emerald-50'
                        : 'border-emerald-500/30 bg-emerald-500/10'
                      : isLight
                        ? 'border-neutral-200'
                        : 'border-neutral-800'
                  }`}
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <span
                      className={`w-5 h-5 rounded-full flex items-center justify-center text-[11px] ${
                        done ? 'bg-emerald-500 text-white' : isLight ? 'bg-neutral-200 text-neutral-600' : 'bg-neutral-800 text-neutral-300'
                      }`}
                    >
                      {done ? <Check size={12} /> : index + 1}
                    </span>
                    <span className={isLight ? 'text-neutral-800' : 'text-neutral-100'}>{step.label}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className={`rounded-xl border p-5 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Globe size={16} className="text-indigo-400" />
            <h3 className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>MCP host</h3>
          </div>
          <p className={`text-sm ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>
            One Streamable HTTP URL for every registered client. Tenant and user come from the MCP JWT.
          </p>
          <div
            className={`mt-4 flex items-start gap-2 rounded-xl border px-3 py-2.5 ${
              isLight ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-800 bg-neutral-950'
            }`}
          >
            <p className={`flex-1 min-w-0 font-mono text-sm break-all ${isLight ? 'text-neutral-900' : 'text-white'}`}>
              {MCP_PUBLIC_URL}
            </p>
            <button type="button" className={ghostBtn(isLight)} onClick={() => void copyUrl()}>
              <Copy size={14} />
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button type="button" className={ghostBtn(isLight)} onClick={() => setSettingsOpen(true)}>
              Host settings
            </button>
            <button type="button" className={primaryBtn()} onClick={() => setTestOpen(true)}>
              <FlaskConical size={14} /> Test MCP connection
            </button>
          </div>
          {host ? (
            <div className="mt-4 space-y-2">
              <ToggleRow
                label="Require authenticated users"
                hint="Reject calls without a valid MCP JWT."
                checked={host.security.requireAuthenticatedUsers}
                onChange={(requireAuthenticatedUsers) => patchSecurity({ requireAuthenticatedUsers })}
                isLight={isLight}
              />
              <ToggleRow
                label="Require registered clients"
                hint="Only clients you register may connect."
                checked={host.security.requireRegisteredClients}
                onChange={(requireRegisteredClients) => patchSecurity({ requireRegisteredClients })}
                isLight={isLight}
              />
            </div>
          ) : null}
        </div>

        <div className={`rounded-xl border p-5 ${isLight ? 'bg-white border-neutral-200' : 'bg-neutral-900 border-neutral-800'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Shield size={16} className="text-indigo-400" />
            <h3 className={`font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Governance reminder</h3>
          </div>
          <ul className={`text-sm space-y-1.5 ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>
            <li>Authenticate → resolve user → validate client → discover authorized capabilities.</li>
            <li>AI clients never decide authorization. Selfic evaluates policy on every call.</li>
            <li>Tool discovery itself is governed. Different users see different catalogs.</li>
          </ul>
          <div className="flex flex-wrap gap-2 mt-4">
            <button type="button" className={ghostBtn(isLight)} onClick={() => openConfiguration('clients')}>
              Register client
            </button>
            <button type="button" className={ghostBtn(isLight)} onClick={() => openConfiguration('policies')}>
              Configure policy
            </button>
          </div>
        </div>
      </div>

      <EndpointFormDrawer
        open={settingsOpen}
        initial={host || null}
        isLight={isLight}
        onClose={() => setSettingsOpen(false)}
        onSave={(endpoint: McpEndpoint) => {
          saveEndpoint(endpoint);
          setSettingsOpen(false);
          onToast('success', 'MCP host settings saved.');
        }}
      />
      <TestConnectionModal
        open={testOpen}
        clients={state.clients}
        isLight={isLight}
        onClose={() => setTestOpen(false)}
        onRun={(clientId) => {
          const event = recordTestConnection(SINGLETON_ENDPOINT_ID, clientId);
          const ok = event.decision === 'allowed';
          onToast(ok ? 'success' : 'error', event.result);
          return { ok, message: event.result };
        }}
      />
    </div>
  );
}
